export const normalizeRoomAllocation = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const alloc = raw
    .map((r) => {
      const room = {
        adults: Math.max(0, Math.trunc(Number(r?.adults) || 0)),
        childrenAges: Array.isArray(r?.childrenAges) ? r.childrenAges.map((a) => Math.trunc(Number(a) || 0)).filter((a) => a >= 0) : [],
      };
      if (r?.roomType) room.roomType = String(r.roomType);
      if (r?.boardType) room.boardType = String(r.boardType);
      return room;
    })
    .filter((r) => r.adults + r.childrenAges.length > 0);
  return alloc.length > 0 ? alloc : null;
};

export const roomAllocationIsHeterogeneous = (alloc) => {
  if (!Array.isArray(alloc) || alloc.length <= 1) return false;
  const key = (r) => r.adults + "|" + [...r.childrenAges].sort((a, b) => a - b).join(",") + "|" + (r.roomType || "") + "|" + (r.boardType || "");
  const first = key(alloc[0]);
  return !alloc.every((r) => key(r) === first);
};

export const roomsFromTotals = (adults, children, childrenAges, roomCount = 1) => {
  const totalAdults = Math.max(1, Number(adults) || 1);
  const ages = Array.from({ length: Math.max(0, Number(children) || 0) }, (_, i) => Number(childrenAges?.[i]) || 0);
  const n = Math.min(Math.max(1, Math.trunc(Number(roomCount) || 1)), totalAdults + ages.length, 4);
  if (n <= 1) return [{ adults: totalAdults, childrenAges: ages }];

  const rooms = Array.from({ length: n }, () => ({ adults: 0, childrenAges: [] }));
  for (let i = 0; i < totalAdults; i++) rooms[i % n].adults += 1;
  // Kinder bevorzugt in Zimmer mit Erwachsenem
  let target = 0;
  for (const age of ages) {
    while (rooms[target].adults === 0 && target < n - 1) target++;
    rooms[target].childrenAges.push(age);
    target = (target + 1) % n;
  }
  return rooms.filter((r) => r.adults + r.childrenAges.length > 0);
};

export const roomAllocationIsMeaningful = (alloc) => Array.isArray(alloc) && (alloc.length > 1 || alloc.some((r) => r.roomType || r.boardType));

// Entfernt roomType/boardType-Vorgaben, die in den aktuell verfuegbaren Optionen
// (region-/hotelabhaengig) nicht vorkommen. Leere Options-Liste = noch nicht geladen
// -> nicht anfassen. Gibt dieselbe Referenz zurueck, wenn nichts zu tun ist.
export const pruneRoomAllocationToOptions = (alloc, roomTypeValues, boardTypeValues) => {
  if (!Array.isArray(alloc)) return alloc;
  const rt = roomTypeValues && roomTypeValues.length > 0 ? new Set(roomTypeValues) : null;
  const bt = boardTypeValues && boardTypeValues.length > 0 ? new Set(boardTypeValues) : null;
  if (!rt && !bt) return alloc;
  let changed = false;
  const out = alloc.map((r) => {
    const n = { ...r };
    if (n.roomType && rt && !rt.has(n.roomType)) {
      delete n.roomType;
      changed = true;
    }
    if (n.boardType && bt && !bt.has(n.boardType)) {
      delete n.boardType;
      changed = true;
    }
    return n;
  });
  return changed ? out : alloc;
};

export const offerIsMultiRoom = (offer) => {
  const rooms = offer?.OfferServices?.Package?.Accommodation?.Rooms ?? offer?.origOffer?.accommodation?.rooms;
  return Array.isArray(rooms) && rooms.length > 1;
};

// Preis je Reisenden aus der besten verfuegbaren Quelle:
// 1. nach Verfuegbarkeitspruefung: bookinginfo.PriceInformation.TravellerPriceList
// 2. sonst der Cache-Preis aus der Suche: origOffer.price.priceByPax (roh von Peakwork)
export const perPaxPrices = (offer) => {
  const checked = offer?.bookinginfo?.PriceInformation?.TravellerPriceList?.TravellerPrice;
  if (Array.isArray(checked) && checked.length > 0) {
    const list = checked.map((t) => ({ amount: Number(t?.Amount?._ ?? t?.Amount), source: "checked" })).filter((t) => Number.isFinite(t.amount));
    if (list.length > 0) return list;
  }
  const cache = offer?.origOffer?.price?.priceByPax;
  if (Array.isArray(cache) && cache.length > 0) {
    return cache.map((p) => ({ amount: Number(p?.amount), personType: p?.personType, source: "cache" })).filter((t) => Number.isFinite(t.amount));
  }
  return [];
};

export const travelerParamsFromSelection = (searchPersons) => {
  const alloc = normalizeRoomAllocation(searchPersons?.roomAllocation);
  const meaningful = roomAllocationIsMeaningful(alloc);
  return {
    roomallocation: meaningful ? alloc : null,
    multiroomdivider: meaningful ? (roomAllocationIsHeterogeneous(alloc) ? 1 : alloc.length) : searchPersons?.rooms || 1,
  };
};
